import { AppState, AppStateStatus, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_CONFIG } from '../config/api';
import authService from './authService';

const STORAGE_KEY = 'analyticsSessionId';
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

class AnalyticsService {
  private sessionId: string | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private appStateSubscription: { remove: () => void } | null = null;
  private visibilityHandler: (() => void) | null = null;
  private pageHideHandler: (() => void) | null = null;
  private isPaused = false;

  async setSessionId(sessionId: string | null): Promise<void> {
    this.sessionId = sessionId;
    if (sessionId) {
      await AsyncStorage.setItem(STORAGE_KEY, sessionId);
    } else {
      await AsyncStorage.removeItem(STORAGE_KEY);
    }
  }

  async loadStoredSessionId(): Promise<string | null> {
    const stored = await AsyncStorage.getItem(STORAGE_KEY);
    this.sessionId = stored;
    return stored;
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  private getClientPlatform(): 'web' | 'ios' | 'android' {
    if (Platform.OS === 'web') {
      return 'web';
    }
    if (Platform.OS === 'ios') {
      return 'ios';
    }
    return 'android';
  }

  private isDocumentHidden(): boolean {
    return Platform.OS === 'web' && typeof document !== 'undefined' && document.hidden;
  }

  private async sendHeartbeat(countElapsed: boolean): Promise<void> {
    if (!authService.isAuthenticated()) {
      return;
    }
    if (authService.getImpersonationChromeState().isImpersonating) {
      return;
    }

    try {
      const response = await authService.makeAuthenticatedRequest<{
        success: boolean;
        data: { sessionId: string; closedForInactivity: boolean };
      }>(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ANALYTICS.HEARTBEAT}`, {
        method: 'POST',
        headers: {
          'X-Client-Platform': this.getClientPlatform(),
        },
        body: JSON.stringify({
          sessionId: this.sessionId ?? undefined,
          countElapsed,
        }),
      });

      if (response.success && response.data.sessionId) {
        await this.setSessionId(response.data.sessionId);
      }
    } catch (error) {
      console.error('Analytics heartbeat failed:', error);
    }
  }

  async flush(): Promise<void> {
    if (!authService.isAuthenticated()) {
      return;
    }
    await this.sendHeartbeat(true);
  }

  private clearTimer(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private startInterval(): void {
    this.clearTimer();
    this.heartbeatTimer = setInterval(() => {
      if (this.isDocumentHidden()) {
        return;
      }
      void this.sendHeartbeat(true);
    }, HEARTBEAT_INTERVAL_MS);
  }

  private async pauseForBackground(): Promise<void> {
    if (this.isPaused) {
      return;
    }
    this.isPaused = true;
    this.clearTimer();
    await this.sendHeartbeat(true);
  }

  private async resumeFromBackground(): Promise<void> {
    this.isPaused = false;
    await this.sendHeartbeat(false);
    this.startInterval();
  }

  async startHeartbeat(): Promise<void> {
    this.stopHeartbeat();
    this.isPaused = false;
    if (authService.getImpersonationChromeState().isImpersonating) {
      return;
    }

    await this.loadStoredSessionId();

    if (this.isDocumentHidden()) {
      this.attachListeners();
      return;
    }

    await this.sendHeartbeat(true);
    this.startInterval();
    this.attachListeners();
  }

  stopHeartbeat(): void {
    this.clearTimer();
    if (this.appStateSubscription) {
      this.appStateSubscription.remove();
      this.appStateSubscription = null;
    }
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.pageHideHandler && typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.pageHideHandler);
      this.pageHideHandler = null;
    }
  }

  private attachListeners(): void {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      this.visibilityHandler = () => {
        if (document.hidden) {
          void this.pauseForBackground();
        } else {
          void this.resumeFromBackground();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
      if (typeof window !== 'undefined') {
        this.pageHideHandler = () => {
          void this.sendHeartbeat(true);
        };
        window.addEventListener('pagehide', this.pageHideHandler);
      }
      return;
    }

    this.appStateSubscription = AppState.addEventListener('change', this.handleAppStateChange);
  }

  private handleAppStateChange = (nextState: AppStateStatus): void => {
    if (nextState === 'active') {
      void this.resumeFromBackground();
      return;
    }
    if (nextState === 'background' || nextState === 'inactive') {
      void this.pauseForBackground();
    }
  };

  async clearSession(): Promise<void> {
    this.stopHeartbeat();
    await this.setSessionId(null);
  }
}

export default new AnalyticsService();
